import React from 'react'
// libs
import { useEditor, EditorContent } from '@tiptap/react'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import FontSize from './extensions/FontSize'
import Color from './extensions/Color'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import StarterKit from '@tiptap/starter-kit'
import FontFamily from '@tiptap/extension-font-family'
import { TextStyle } from "@tiptap/extension-text-style"
// types
import { Editor } from '@tiptap/react'
import { ToolbarOption } from 'components/FormElements/Wysiwyg/types'
// components
import Toolbar from './Toolbar'
// constants
import { WYSIWIG_CLASS_NAMES } from 'constants/index'
// views
import { WysiwygWrapper } from 'components/FormElements/Wysiwyg/views'

interface WysiwygProps {
  value?: string
  toolbar?: { options?: ToolbarOption[] | readonly ToolbarOption[] }
  onChange?: (value: string) => void
  readOnly?: boolean
  editorRef?: (editor: Editor | null) => void
  placeholder?: string
  toolbarCustomButtons?: React.ReactNode[]
}

const Wysiwyg: React.FC<WysiwygProps> = ({ value, onChange, readOnly, toolbar, toolbarCustomButtons, editorRef }) => {
  const isInternalUpdate = React.useRef(false)

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'right', 'center', 'justify', 'start', 'end'] }),
      Image.configure({ allowBase64: true })
    ],
    content: value ?? '',
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true
      onChange?.(editor.getHTML())
    },
    editable: !readOnly
  })

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false
      return
    }
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value ?? '')
    }
  }, [value])

  React.useEffect(() => {
    editorRef?.(editor)
  }, [editor])

  const toolbarOptions = toolbar?.options

  return (
    <WysiwygWrapper className={WYSIWIG_CLASS_NAMES.wrapperClassName}>
      <Toolbar
        editor={editor}
        hidden={readOnly}
        options={toolbarOptions}
        customButtons={toolbarCustomButtons}
      />
      <EditorContent editor={editor} className={WYSIWIG_CLASS_NAMES.editorClassName} />
    </WysiwygWrapper>
  )
}

export default Wysiwyg
